import React, { useState } from "react";
import {
  Dialog,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ImageGalleryComponent from "../images/ImageGalleryComponent";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import {
  obtenerFotosTipoHab,
  obtenerDatosHotel,
  obtenerMuestroTarifaTipoHab,
  obtenerTarifaPorTipoCliente,
  tarifaNoLineal,
} from "../../services/motorService";
import Cookies from "js-cookie";
import {
  TableCell,
  TableHead,
  Table,
  TableRow,
  TableBody,
} from "@mui/material";
import { useLocation } from "react-router-dom";

const pad2 = (v) => String(v).padStart(2, "0");

const toYmd = (value) => {
  if (!value) return "";

  if (value?.format && typeof value.format === "function") {
    return value.format("YYYY-MM-DD");
  }

  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(
      parsed.getDate()
    )}`;
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

export default function ResponsiveDialog({
  open,
  handleClose,
  ratePlan,
  selectedRange,
  room,
  totalAdults,
  totalChildren,
  tipoTarifaABuscar,
  cliente,
}) {
  const [nightlyRates, setNightlyRates] = React.useState([]);
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hotcodParam = params.get("hotcod");
  const hotCod = hotcodParam ?? Cookies.get("hotCod") ?? null;
  const [rateCoin, setRateCoin] = useState("");
  const [datosHotel, setDatosHotel] = useState({});
  const isMobile = useMediaQuery("(max-width:600px)");

  const hasAM = React.useMemo(() => {
    const arr = ratePlan?.ratePlan?.ratePlansPorRegimen;
    return (
      Array.isArray(arr) &&
      arr.some((rp) => rp.TarifaAplica === "A" || rp.TarifaAplica === "M")
    );
  }, [ratePlan]);

  const isNoLineal = React.useMemo(() => {
    const arr = ratePlan?.ratePlan?.ratePlansPorRegimen;
    console.log("ARRRR:", arr);
    return Array.isArray(arr) && arr.some((rp) => rp.TarifaAplica === "N");
  }, [ratePlan]);

  const normalizeImages = (imagesResponse) => {
    const out = [];

    const pushImage = (raw) => {
      if (!raw || typeof raw !== "string") return;
      let src = raw.trim();

      if (!/^data:image\//i.test(src) && !/^https?:\/\//i.test(src)) {
        src = `data:image/jpeg;base64,${src}`;
      }

      out.push({ original: src, thumbnail: src });
    };

    const walk = (val) => {
      if (!val) return;

      if (Array.isArray(val)) {
        val.forEach(walk);
        return;
      }

      if (typeof val === "string") {
        pushImage(val);
        return;
      }

      if (typeof val === "object") {
        const candidates = [
          val.image,
          val.Image,
          val.imagen,
          val.Imagen,
          val.foto,
          val.Foto,
          val.url,
          val.URL,
          val.src,
          val.Src,
          val.thumbnail,
          val.Thumbnail,
          val.base64,
          val.Base64,
          val.imageBase64,
          val.ImagenBase64,
          val.FotoBase64,
        ];

        for (const c of candidates) {
          if (c) pushImage(c);
        }

        for (const key of Object.keys(val)) {
          const k = key.toLowerCase();
          if (
            k.includes("image") ||
            k.includes("images") ||
            k.includes("foto") ||
            k.includes("fotos") ||
            k.includes("imagen") ||
            k.includes("imagenes") ||
            k.includes("items") ||
            k.includes("lista") ||
            k.includes("list")
          ) {
            walk(val[key]);
          }
        }
      }
    };

    walk(imagesResponse);
    return out;
  };

  const fallbackFromRoom = () => {
    const candidates = [
      room?.image,
      room?.Image,
      room?.imagen,
      room?.Imagen,
      room?.foto,
      room?.Foto,
      room?.base64,
      room?.imageBase64,
      room?.ImagenBase64,
      room?.FotoBase64,
    ].filter(Boolean);

    if (candidates.length === 0) return [];

    const first = candidates[0];
    const src =
      typeof first === "string" &&
        (/^https?:\/\//i.test(first) || /^data:image\//i.test(first))
        ? first
        : `data:image/jpeg;base64,${first}`;

    return [{ original: src, thumbnail: src }];
  };

  React.useEffect(() => {
    const getData = async () => {
      try {
        const data = await obtenerDatosHotel(hotCod);
        setDatosHotel(data);
      } catch (error) {
        console.error("Error fetching hotel data:", error);
      }
    };
    getData();
  }, [hotCod]);

  React.useEffect(() => {
    if (ratePlan && selectedRange) {
      fetchNightlyRates(
        ratePlan?.ratePlan?.ratePlan?.TarifaTipoHab,
        selectedRange
      );
      setRateCoin(ratePlan?.ratePlan?.ratePlan?.TarifaMonDsc);
    }

    const fetchImages = async () => {
      try {
        setImagesLoading(true);
        setImages([]);

        const tipoHab = ratePlan?.ratePlan?.ratePlan?.TarifaTipoHab;
        if (!hotCod || !tipoHab) {
          const fallback = fallbackFromRoom();
          if (fallback.length > 0) setImages(fallback.slice(0, 4));
          setImagesLoading(false);
          return;
        }

        const imagesResponse = await obtenerFotosTipoHab(hotCod, tipoHab);
        let normalized = normalizeImages(imagesResponse);

        if (!normalized || normalized.length === 0) {
          const fallback = fallbackFromRoom();
          if (fallback.length > 0) {
            normalized = fallback;
          } else {
            normalized = [
              {
                original: "https://via.placeholder.com/1000x600?text=Sin+fotos",
                thumbnail: "https://via.placeholder.com/300x180?text=Sin+fotos",
              },
            ];
          }
        }

        setImages(normalized.slice(0, 8));
      } catch (err) {
        console.error("Error fetching images:", err);
        const fallback = fallbackFromRoom();
        setImages(
          fallback.length > 0
            ? fallback
            : [
              {
                original: "https://via.placeholder.com/1000x600?text=Sin+fotos",
                thumbnail: "https://via.placeholder.com/300x180?text=Sin+fotos",
              },
            ]
        );
      } finally {
        setImagesLoading(false);
      }
    };

    if (open) {
      fetchImages();
    } else {
      setImages([]);
    }
  }, [ratePlan, selectedRange, open, room, hotCod]);

  const fetchNightlyRates = async (tipoHab, selectedRange) => {
    try {
      const startDate = new Date(selectedRange.startDate);
      const endDate = new Date(selectedRange.endDate);

      const nightlyRates = [];
      let currentDate = new Date(startDate);

      const codigoTarifaAdmintour =
        ratePlan?.ratePlan?.ratePlan?.TarifaCodAdm;

      const n = (x) => {
        if (x == null) return 0;
        const cleaned = String(x).replace(/\./g, "").replace(",", ".");
        const v = parseFloat(cleaned);
        return Number.isFinite(v) ? v : 0;
      };

      const calcNoLinealTotal = ({ adults, children, nl }) => {
        const t1 = n(nl?.tarifa1);
        const t2 = n(nl?.tarifa2);
        const t3 = n(nl?.tarifa3);
        const t4 = n(nl?.tarifa4);
        const t5 = n(nl?.tarifa5);
        const m1 = n(nl?.menor1);
        const m2 = n(nl?.menor2);
        const add = n(nl?.tarifapaxadicional);

        console.log("TARIFASSSSSS:", t1, t2, t3, t4, t5, m1, m2, add);

        let totalAdult = 0;
        if (adults <= 0) totalAdult = 0;
        else if (adults === 1) totalAdult = t1;
        else if (adults === 2) totalAdult = t2;
        else if (adults === 3) totalAdult = t3;
        else if (adults === 4) totalAdult = t4;
        else if (adults === 5) totalAdult = t5;
        else totalAdult = t5 + (adults - 5) * add;

        let totalChild = 0;
        if (children <= 0) totalChild = 0;
        else if (children === 1) totalChild = m1;
        else if (children === 2) totalChild = m2;
        else totalChild = children * m1;

        return totalAdult + totalChild;
      };

      while (currentDate < endDate) {
        const formattedDate = toYmd(currentDate);

        let respuesta = [];

        if (tipoTarifaABuscar === "H") {
          const r = await obtenerMuestroTarifaTipoHab(
            hotCod,
            ratePlan?.ratePlan?.ratePlan?.TarifaTipoHab,
            formattedDate
          );
          respuesta = r?.TarifaTipoHabItem ?? [];
        } else if (tipoTarifaABuscar === "C") {
          const r = await obtenerTarifaPorTipoCliente(
            hotCod,
            ratePlan?.ratePlan?.ratePlan?.TarifaTipoHab,
            formattedDate,
            cliente
          );
          respuesta = r?.TarifaTipoHabItem ?? [];
        }

        if (!Array.isArray(respuesta)) respuesta = [];

        if (isNoLineal) {
          const hotelCodAdm = datosHotel?.MotorHotAdmintour;
          const codigoTarifaAdmintour =
            ratePlan?.ratePlan?.ratePlan?.TarifaCodAdm;

          const nl = await tarifaNoLineal(
            hotelCodAdm,
            codigoTarifaAdmintour,
            formattedDate
          );

          const totalNight = calcNoLinealTotal({
            adults: totalAdults,
            children: totalChildren,
            nl,
          });

          console.log("TotalNight", totalNight);
          console.log(
            "descripcion moneda",
            ratePlan?.ratePlan?.ratePlan?.TarifaMonDsc
          );

          nightlyRates.push({
            date: formattedDate,
            totalNight,
            coin: ratePlan?.ratePlan?.ratePlan?.TarifaMonDsc || "N/A",
          });

          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }

        if (hasAM) {
          const adultRp = ratePlan?.ratePlan?.ratePlansPorRegimen?.find(
            (rp) => rp.TarifaAplica === "A"
          );
          const childRp = ratePlan?.ratePlan?.ratePlansPorRegimen?.find(
            (rp) => rp.TarifaAplica === "M"
          );

          const adultCod = adultRp?.TarifaCodAdm;
          const childCod = childRp?.TarifaCodAdm;

          const adultItem = respuesta.find(
            (it) => String(it.TarifaCodAdm) === String(adultCod)
          );
          const childItem = respuesta.find(
            (it) => String(it.TarifaCodAdm) === String(childCod)
          );

          const adultRate =
            adultItem?.TarifaPrecioDesde ??
            adultRp?.TarifaPrecioDesde ??
            adultRp?.TarfaPrecioMinimo ??
            0;

          const childRate =
            childItem?.TarifaPrecioDesde ??
            childRp?.TarifaPrecioDesde ??
            childRp?.TarfaPrecioMinimo ??
            0;

          nightlyRates.push({
            date: formattedDate,
            adultRate,
            childRate,
            coin:
              adultRp?.TarifaMonDsc ||
              childRp?.TarifaMonDsc ||
              ratePlan?.ratePlan?.ratePlan?.TarifaMonDsc ||
              "N/A",
          });
        } else {
          const matching = respuesta.find(
            (item) =>
              String(item.TarifaCodAdm) === String(codigoTarifaAdmintour)
          );

          nightlyRates.push({
            date: formattedDate,
            rate:
              matching?.TarifaPrecioDesde ??
              ratePlan?.ratePlan?.ratePlan?.TarifaPrecioDesde ??
              ratePlan?.ratePlan?.ratePlan?.TarfaPrecioMinimo ??
              0,
            coin: ratePlan?.ratePlan?.ratePlan?.TarifaMonDsc || "N/A",
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      setNightlyRates(nightlyRates);
    } catch (error) {
      console.error("Error fetching nightly rates:", error);
    }
  };

  /*const formatDate = (date) => {
    const parsedDate = new Date(date);
    const day = parsedDate.getUTCDate();
    const month = parsedDate.getUTCMonth() + 1;
    const year = parsedDate.getUTCFullYear();
    return `${day.toString().padStart(2, "0")}/${month
      .toString()
      .padStart(2, "0")}/${year}`;
  };*/
  const formatDate = (value) => {
    if (!value) return "";

    // Si ya viene como YYYY-MM-DD
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}/${month}/${year}`;
    }

    // dayjs / moment-like
    if (value?.format && typeof value.format === "function") {
      return value.format("DD/MM/YYYY");
    }

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const totalAmount = nightlyRates
    .reduce((total, rate) => {
      if (isNoLineal) {
        return total + parseFloat(rate.totalNight || 0);
      }
      if (hasAM) {
        const adultTotal = parseFloat(rate.adultRate || 0) * totalAdults;
        const childTotal = parseFloat(rate.childRate || 0) * totalChildren;
        return total + adultTotal + childTotal;
      }
      return total + parseFloat(rate.rate || 0);
    }, 0)
    .toFixed(2);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          padding: fullScreen ? "8px" : "0px",
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {ratePlan?.TarifaDescripcion}
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DialogContent>
          {imagesLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : images && images.length > 0 ? (
            <ImageGalleryComponent images={images} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
              <Typography variant="body2" color="text.secondary">
                Sin fotos disponibles
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogContent>
          <DialogContentText sx={{ fontWeight: "bold" }}>
            Desglose de la tarifa desde {formatDate(selectedRange.startDate)} hasta{" "}
            {formatDate(selectedRange.endDate)}
          </DialogContentText>

          {isNoLineal ? (
            !isMobile ? (
              <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Fecha
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                        Total por noche
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nightlyRates.map((nightlyRate, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(nightlyRate.date)}</TableCell>
                      <TableCell align="center">
                        {`${nightlyRate.coin} ${parseFloat(
                          nightlyRate.totalNight || 0
                        ).toLocaleString("es-ES", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              nightlyRates.map((nightlyRate, index) => (
                <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 3 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {formatDate(nightlyRate.date)}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      Total por noche:{" "}
                      {`${nightlyRate.coin} ${parseFloat(
                        nightlyRate.totalNight || 0
                      ).toLocaleString("es-ES", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            )
          ) : hasAM ? (
            <>
              {!isMobile ? (
                <Table sx={{ width: "100%", borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          Fecha
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          Adultos
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          Menores
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          Total por noche
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {nightlyRates.map((nightlyRate, index) => {
                      const adultRate = parseFloat(nightlyRate.adultRate || 0);
                      const childRate = parseFloat(nightlyRate.childRate || 0);
                      const totalNight =
                        adultRate * totalAdults +
                        (totalChildren > 0 ? childRate * totalChildren : 0);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDate(nightlyRate.date)}</TableCell>
                          <TableCell align="center">
                            {`${nightlyRate.coin} ${(adultRate * totalAdults).toLocaleString(
                              "es-ES",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}`}
                          </TableCell>
                          <TableCell align="center">
                            {totalChildren > 0
                              ? `${nightlyRate.coin} ${(childRate * totalChildren).toLocaleString(
                                "es-ES",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}`
                              : "-"}
                          </TableCell>
                          <TableCell align="center">
                            {`${nightlyRate.coin} ${totalNight.toLocaleString("es-ES", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                nightlyRates.map((nightlyRate, index) => {
                  const adultRate = parseFloat(nightlyRate.adultRate || 0);
                  const childRate = parseFloat(nightlyRate.childRate || 0);
                  const totalNight =
                    adultRate * totalAdults +
                    (totalChildren > 0 ? childRate * totalChildren : 0);

                  return (
                    <Card key={index} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 3 }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                          {formatDate(nightlyRate.date)}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2">
                          <strong>Adultos:</strong>{" "}
                          {`${nightlyRate.coin} ${(adultRate * totalAdults).toLocaleString(
                            "es-ES",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}`}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Menores:</strong>{" "}
                          {totalChildren > 0
                            ? `${nightlyRate.coin} ${(childRate * totalChildren).toLocaleString(
                              "es-ES",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}`
                            : "-"}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          Total por noche:{" "}
                          {`${nightlyRate.coin} ${totalNight.toLocaleString("es-ES", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </>
          ) : (
            <>
              {nightlyRates &&
                nightlyRates.map((nightlyRate, index) => (
                  <Grid container key={index}>
                    <Grid item xs={6}>
                      <DialogContentText>{formatDate(nightlyRate.date)}</DialogContentText>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <DialogContentText>
                        {nightlyRate.coin}{" "}
                        {parseFloat(nightlyRate.rate || 0).toLocaleString("es-ES", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </DialogContentText>
                    </Grid>
                  </Grid>
                ))}
            </>
          )}

          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              alignItems: "center",
            }}
          >
            <DialogContentText sx={{ fontWeight: "bold" }}>Total</DialogContentText>
            <DialogContentText sx={{ fontWeight: "bold", textAlign: "right" }}>
              {`${rateCoin} ${parseFloat(totalAmount).toLocaleString("es-ES", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            </DialogContentText>
          </Box>
        </DialogContent>
      </DialogContent>
    </Dialog>
  );
}